import { Group, Schedule } from '@mui/icons-material';
import { Alert, Box, Typography } from '@mui/material';
import { getMissingApprovals } from 'lib/api/approvals';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import ApprovalHistoryCard from './ApprovalHistoryCard';
import ApprovalRejectionDialog from './ApprovalRejectionDialog';
import ApprovalRoleCard from './ApprovalRoleCard';
import { useComiteeTabContext } from './ComiteeTabProvider';

const CommitteeTabs = () => {
  const {
    requestType,
    requestId,
    comiteeType,
    isActive,
    canEdit,
    setSelectedRoleId,
  } = useComiteeTabContext();

  const { data: missingApprovals } = getMissingApprovals(
    useDispatch(),
    requestType,
    requestId,
    comiteeType,
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState<'APPROVED' | 'REJECTED'>(
    'APPROVED',
  );
  const [table, setTable] = useState(isActive ? 'approvals' : 'comitee');

  const roles = missingApprovals?.roles ?? [];

  const totalRequiredApprovals = roles.reduce((total, r) => {
    total += r.requiredApprovals;
    return total;
  }, 0);

  const totalApprovals = roles.reduce((total, r) => {
    total += r.approved.length;
    return total;
  }, 0);

  const approvals = roles.flatMap((r) => [...r.approved, ...r.rejected]);

  const userRoles = roles
    .filter((r) => r.userCanApprove)
    .map((r) => ({ id: r.roleId, name: r.roleName }));

  const missingApprovalsNumber = totalRequiredApprovals - totalApprovals;

  const handleApproveClick = (roleId: number) => {
    setDialogType('APPROVED');
    setSelectedRoleId(roleId);
    setDialogOpen(true);
  };

  const handleRejectClick = (roleId: number) => {
    setDialogType('REJECTED');
    setSelectedRoleId(roleId);
    setDialogOpen(true);
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
    setSelectedRoleId(undefined);
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        sx={{
          display: 'flex',
          gap: 0,
          backgroundColor: '#f5f5f5',
          borderRadius: 2,
          p: 0.5,
          mb: 3,
        }}
      >
        {!isActive && (
          <Box
            onClick={() => setTable('comitee')}
            sx={{
              flex: 1,
              py: 1.5,
              px: 2,
              borderRadius: 1.5,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              backgroundColor: table === 'comitee' ? 'white' : 'transparent',
              boxShadow:
                table === 'comitee' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor:
                  table === 'comitee' ? 'white' : 'rgba(255,255,255,0.5)',
              },
            }}
          >
            <Group
              sx={{
                fontSize: '1.1rem',
                color: table === 'comitee' ? 'text.main' : 'text.secondary',
              }}
            />
            <Typography
              variant="body2"
              sx={{
                fontWeight: table === 'comitee' ? 600 : 500,
                color: table === 'comitee' ? 'text.primary' : 'text.secondary',
                fontSize: '0.9rem',
              }}
            >
              Aprobaciones Pendientes
            </Typography>
            {missingApprovalsNumber > 0 && (
              <Box
                sx={{
                  backgroundColor: 'warning.main',
                  color: 'white',
                  borderRadius: '50%',
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  ml: 0.5,
                }}
              >
                {missingApprovalsNumber}
              </Box>
            )}
          </Box>
        )}
        <Box
          onClick={() => setTable('approvals')}
          sx={{
            flex: 1,
            py: 1.5,
            px: 2,
            borderRadius: 1.5,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            backgroundColor: table === 'approvals' ? 'white' : 'transparent',
            boxShadow:
              table === 'approvals' ? '0 2px 8px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor:
                table === 'approvals' ? 'white' : 'rgba(255,255,255,0.5)',
            },
          }}
        >
          <Schedule
            sx={{
              fontSize: '1.1rem',
              color: table === 'approvals' ? 'text.main' : 'text.secondary',
            }}
          />
          <Typography
            variant="body2"
            sx={{
              fontWeight: table === 'approvals' ? 600 : 500,
              color: table === 'approvals' ? 'text.primary' : 'text.secondary',
              fontSize: '0.9rem',
            }}
          >
            Historial
          </Typography>
          {totalApprovals > 0 && (
            <Box
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                borderRadius: '50%',
                width: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.7rem',
                fontWeight: 600,
                ml: 0.5,
              }}
            >
              {totalApprovals}
            </Box>
          )}
        </Box>
      </Box>

      {table === 'comitee' ? (
        <Box>
          {missingApprovals?.isFullyApproved ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                Todos los roles han sido aprobados correctamente.
              </Typography>
            </Alert>
          ) : (
            <>
              {roles.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Debes asignar al menos un rol al comité para aprobar la
                    operación.
                  </Typography>
                </Alert>
              ) : (
                roles.map((role, index) => (
                  <ApprovalRoleCard
                    key={index}
                    role={role}
                    index={index}
                    onApprove={handleApproveClick}
                    onReject={handleRejectClick}
                    canEdit={canEdit}
                  />
                ))
              )}
            </>
          )}
        </Box>
      ) : (
        <Box>
          {approvals.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                No hay aprobaciones registradas aún.
              </Typography>
            </Alert>
          ) : (
            approvals.map((approval, index) => (
              <ApprovalHistoryCard
                key={index}
                approval={approval}
                index={index}
              />
            ))
          )}
        </Box>
      )}

      <ApprovalRejectionDialog
        open={dialogOpen}
        type={dialogType}
        onCancel={handleDialogCancel}
        setOpen={setDialogOpen}
        userRoles={userRoles}
      />
    </Box>
  );
};

export default CommitteeTabs;
